import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import s from "./HelpPage.module.css";

function getUser() {
  try {
    return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null");
  } catch { return null; }
}

/**
 * Per-role Help content. Each role sees ONLY guides relevant to what it can
 * actually do — Cashier doesn't need enrollment steps, Registrar doesn't need
 * payment-recording steps, etc. Admin / SuperAdmin see the full catalog.
 *
 * Shape:
 *   role → [ { id, icon, title, summary, steps: string[], links: [{label, to}] } ]
 *
 * Paths must match real routes defined in App.jsx so the CTA links actually
 * go somewhere the user is allowed to visit.
 */
const GUIDES = {
  // ── Admissions & enrollment ─────────────────────────────────────────
  enroll: {
    icon: "🎓",
    title: "Enroll a new student",
    summary: "Walk the Admissions wizard end-to-end: student details, parents, section, fee structure, discounts, and documents. The wizard generates the first tuition invoice automatically on Save.",
    steps: [
      "Open Admissions from the top nav.",
      "Step 1 · Student — fill in the student number, name, birthdate, sex, contact, and address. The student number auto-fills from the active prefix.",
      "Step 2 · Parents — add one or more parents/guardians. You can pick an existing parent record or create a new one; mark one as Primary.",
      "Step 3 · Enrollment — pick the school year, semester, grade level, and section. Type a new name in any dropdown to create it on the fly.",
      "Step 4 · Tuition — pick an existing fee structure for the grade + school year, or create a new one. The total is derived from Tuition + Misc + Books.",
      "Step 5 · Discounts — optional. Admin/SuperAdmin only; Registrars see a stub and should ask an admin to add discounts after the fact.",
      "Step 6 · Documents — upload required files (birth certificate, Form 137, IDs, etc.). Stored on S3.",
      "Review the summary, then click Save. A tuition invoice is generated for the selected semester.",
    ],
    links: [{ label: "Open Admissions", to: "/admissions/new" }],
  },
  directory: {
    icon: "📇",
    title: "Find a student or parent",
    summary: "Search the student / parent directory, open a record, and edit their details.",
    steps: [
      "Open People → Students & Parents from the top nav.",
      "Use the search box (name or student number) to filter.",
      "Click a row to open the profile.",
      "Use the Edit button inside the profile to change details.",
    ],
    links: [{ label: "Open Directory", to: "/directory" }],
  },

  // ── Tuition & payments (Cashier) ────────────────────────────────────
  recordPayment: {
    icon: "₱",
    title: "Record a tuition payment",
    summary: "Log a cash / bank / GCash / card payment against a student's invoice. The system auto-allocates across Enrollment + month rows.",
    steps: [
      "Open Operations → Tuition from the top nav.",
      "Switch to the Payments tab.",
      "Use the School Year / Semester / search filters to find the student's invoice row.",
      'Click the "Pay" button on the invoice row to open the Record Payment modal.',
      'Pick the "Applies to" target — leave on "Auto" for FIFO allocation, or pick a specific month / enrollment row.',
      "Enter the amount, pick the Method (Cash hides Reference # automatically; Bank transfer adds a Bank Name dropdown).",
      "Add a reference number (check / OR / GCash ref) and optional notes.",
      "Click Confirm Payment. The invoice status updates (UNPAID → PARTIAL → PAID) and the payment appears in the history.",
    ],
    links: [{ label: "Open Tuition", to: "/admin/tuition" }],
  },
  paymentHistory: {
    icon: "📋",
    title: "View an invoice's payment history",
    summary: "See every payment + recompute event on one invoice, plus what each payment covered (Enrollment + months).",
    steps: [
      "Open Operations → Tuition → Payments tab.",
      "Find the student's invoice row.",
      "Click the clipboard icon (📋) to open Payment History.",
      'Each row in the Activity Log shows amount, method, bank, reference #, who processed it, and a "Covers:" line with the month allocation.',
      'To reverse a mistaken payment, click Delete (Admin / SuperAdmin only) — it writes a reversal and the status reverts.',
    ],
    links: [{ label: "Open Tuition", to: "/admin/tuition" }],
  },
  refund: {
    icon: "↩️",
    title: "Issue a refund for an overpayment",
    summary: "When an invoice is overpaid, issue a refund. It's stored as a negative payment so it appears in the invoice history.",
    steps: [
      "Open Operations → Tuition → Payments tab.",
      "Look for invoices with an Overpaid badge.",
      "Click Refund on that row.",
      "The modal pre-fills the refund amount with the overpaid delta; adjust if only partial.",
      "Pick Method and add a Reason. Click Confirm. The refund appears alongside receipts in the activity log.",
    ],
    links: [{ label: "Open Tuition", to: "/admin/tuition" }],
  },
  statementOfAccount: {
    icon: "📄",
    title: "Print a Statement of Account",
    summary: "Generate the NCLA-style SOA for a student showing fees, discounts, monthly dues, and payment history.",
    steps: [
      "Open Operations → Statement of Account from the top nav.",
      "Pick the student (search by name or student number).",
      "Pick the school year — the SOA renders automatically.",
      "Click Download PDF to export. The PDF strips admin-only controls.",
    ],
    links: [{ label: "Open Statement of Account", to: "/tuition/soa" }],
  },

  // ── Tuition admin (Admin only) ──────────────────────────────────────
  recompute: {
    icon: "🔄",
    title: "Recompute an invoice after a change",
    summary: "If you change the fee structure or add a discount, click Recompute on each affected invoice to update the amount. Payments are never touched — only the amount is re-derived.",
    steps: [
      "Open Operations → Tuition → Invoices tab.",
      "Find the affected invoice row.",
      "Click Recompute. A preview shows the old amount vs. the new amount.",
      "Type a reason (e.g. 'Sibling discount added') and confirm. An InvoiceAdjustment row is written for the audit trail.",
      'Use "Recompute All" (top-right) to batch-process every invoice in the current SY/Semester filter.',
    ],
    links: [{ label: "Open Tuition", to: "/admin/tuition" }],
  },
  feeStructure: {
    icon: "📐",
    title: "Create or edit a fee structure",
    summary: "Define Tuition / Misc / Books / Enrollment amounts for a grade level + school year. Used by every invoice generated for that combination.",
    steps: [
      "Open Operations → Tuition → Fee Structures tab.",
      'Click + New to add a new structure, or click a row to Edit.',
      "Fill in the grade, school year, and the 4 fee components. The total updates automatically.",
      'Save. Existing invoices for that grade + SY do not update automatically — use Recompute to roll out the change.',
    ],
    links: [{ label: "Open Tuition", to: "/admin/tuition" }],
  },
  discount: {
    icon: "🏷️",
    title: "Add or edit a student discount",
    summary: "Student-specific discounts (Sibling, Honors, etc.) that apply to one or more fee components. Strict Admin/SuperAdmin only.",
    steps: [
      "Open Operations → Tuition → Discounts tab.",
      "Click a student's row to open the Manage Discounts modal.",
      'Pick a discount Type (duplicates are disabled; OTHERS is the catch-all for one-off discounts).',
      "Enter the percentage and pick which fees it applies to (Tuition / Misc / Books).",
      "Click Add Discount. To edit an existing one, click Edit next to the row.",
      "Deleting or editing a discount triggers an automatic recompute for every invoice in that student's school year.",
    ],
    links: [{ label: "Open Tuition", to: "/admin/tuition" }],
  },

  // ── Reports ─────────────────────────────────────────────────────────
  reports: {
    icon: "📊",
    title: "Open the Finance report",
    summary: "See total tuition received, method breakdown (GCash, Bank per-bank, Cash), and per-student totals for a chosen time window.",
    steps: [
      "Open Reports from the top nav.",
      "Pick a Window (Today / 7 days / 30 days / 90 days / 12 months / All time).",
      "The Tuition by payment method card shows a card per method; Bank transfers split by bank name (e.g. BANK · BDO).",
      'Inside each method card, students are ranked by total paid, highest first.',
      'Click the "i" on any payment in the Recent list to see the full payment details.',
    ],
    links: [{ label: "Open Reports", to: "/reports" }],
  },

  // ── Academics (Academic Head + Admin) ───────────────────────────────
  assignTeacher: {
    icon: "👩‍🏫",
    title: "Assign a teacher to a class offering",
    summary: "Create a class offering for a section and assign the teacher who will handle it.",
    steps: [
      "Open Academics from the top nav.",
      "Switch to the Class Offerings tab.",
      'Click + New. Pick the subject, section, teacher, schedule, and room.',
      "Save. The teacher shows up in the Dashboard's Teaching Load panel.",
    ],
    links: [{ label: "Open Academics", to: "/academics" }],
  },
  section: {
    icon: "🏫",
    title: "Create a section",
    summary: "A section is a grouping of students under one grade level for a school year.",
    steps: [
      "Open Academics → Sections tab.",
      "Click + New. Pick the school year, grade level, adviser, and section name.",
      'Save. The section becomes available in the Admissions "Section" dropdown.',
    ],
    links: [{ label: "Open Academics", to: "/academics" }],
  },

  // ── Settings (SuperAdmin) ───────────────────────────────────────────
  addUser: {
    icon: "👤",
    title: "Create a user account",
    summary: "Give a staff member / student / parent login access. Each account is linked to a real person record.",
    steps: [
      "Open Settings → Users tab.",
      'Click "Link to a person" to pick the Staff / Student / Parent record.',
      "Fill in a username, email, and temporary password.",
      "Role auto-fills based on the category; adjust for staff if needed.",
      "Click Create user. Share the credentials with the person — they can change the password later.",
    ],
    links: [{ label: "Open Settings", to: "/settings" }],
  },
  configBanks: {
    icon: "🏦",
    title: "Manage the accepted banks list",
    summary: "The Bank Name dropdown in Record Payment is populated from this list. Keep it current to avoid typo drift in the history.",
    steps: [
      "Open Settings → Statement of Account tab.",
      "Scroll to Accepted Banks.",
      'Type a bank name (e.g. "BDO", "BPI", "LandBank") and click Add bank.',
      "Remove an entry with the Remove button. Case-insensitive dedupe prevents duplicates.",
    ],
    links: [{ label: "Open Settings", to: "/settings" }],
  },
  configSoaFooter: {
    icon: "✍️",
    title: "Set the SOA officer + footer note",
    summary: "The officer name, title, and footer note that appears on every printed Statement of Account.",
    steps: [
      "Open Settings → Statement of Account tab.",
      'Fill Officer name, Officer title, and the Footer note.',
      "Click Save footer. Every SOA render/PDF uses the new values immediately.",
    ],
    links: [{ label: "Open Settings", to: "/settings" }],
  },
  backup: {
    icon: "💾",
    title: "Back up or restore the database",
    summary: "Export a full JSON snapshot (or restore one). Restore WIPES every row before loading — use carefully.",
    steps: [
      "Open Settings → Maintenance tab.",
      "Click Download backup. A JSON file is saved to your computer. Keep it somewhere safe — it contains hashed passwords and personal data.",
      'To restore, pick the file, type "WIPE AND RESTORE", and click Wipe & restore.',
      'To start fresh without a restore, use Wipe only — it clears every table but keeps SuperAdmin accounts.',
    ],
    links: [{ label: "Open Settings", to: "/settings" }],
  },

  // ── Common / viewer-side ────────────────────────────────────────────
  viewMyTuition: {
    icon: "💳",
    title: "View your tuition balance",
    summary: "See your invoices, running balance, and payment history.",
    steps: [
      "Open Tuition from the top nav.",
      "Your invoices for the current school year show at the top.",
      'Click an invoice to see the full payment history.',
      'For a printable Statement of Account, open Statement of Account.',
    ],
    links: [
      { label: "Open Tuition", to: "/tuition" },
      { label: "Open Statement of Account", to: "/tuition/soa" },
    ],
  },
  viewChildTuition: {
    icon: "👨‍👩‍👦",
    title: "View your child's tuition",
    summary: "If you have multiple linked children, pick one using the child switcher at the top.",
    steps: [
      "Open Tuition from the top nav.",
      'If you have multiple children linked, pick the one you want to view using the row of child buttons at the top.',
      "Their invoices + payment history load automatically.",
      'Use Statement of Account for the full printable sheet.',
    ],
    links: [
      { label: "Open Tuition", to: "/tuition" },
      { label: "Open Statement of Account", to: "/tuition/soa" },
    ],
  },
  viewSchedule: {
    icon: "📅",
    title: "View your class schedule",
    summary: "See your class offerings for the current semester — subject, section, room, and time.",
    steps: [
      "Open Calendar → Class Schedule from the top nav.",
      "The current semester's offerings load by default.",
    ],
    links: [{ label: "Open Schedule", to: "/schedule" }],
  },
  announcements: {
    icon: "📣",
    title: "Read announcements",
    summary: "School-wide announcements appear on the Announcements page and on your dashboard.",
    steps: [
      "Open Announcements from the top nav.",
      "Click a card to read the full announcement.",
    ],
    links: [{ label: "Open Announcements", to: "/announcements" }],
  },
  calendar: {
    icon: "🗓️",
    title: "See upcoming events",
    summary: "The school calendar lists events (holidays, exams, meetings, etc.).",
    steps: [
      "Open Calendar → Events Calendar from the top nav.",
      "Switch between Month / Week / Day views.",
    ],
    links: [{ label: "Open Calendar", to: "/calendar" }],
  },

  // ── Multi-branch / chain admin ──────────────────────────────────────
  switchBranch: {
    icon: "🏫",
    title: "Switch between school branches",
    summary: "Chain-tier roles (SuperDev / SuperAdmin / Owner) can switch the topbar to any branch and see that branch's data. Branch admins stay pinned to their own school.",
    steps: [
      "Look at the school pill on the right of the top bar (next to your name).",
      "Click the pill to open the branch dropdown — every active branch is listed.",
      "Pick a branch. The page reloads scoped to that branch — every read endpoint now filters by it automatically.",
      "To see the chain-wide overview (KPIs per branch), open Settings → Branches → Overview.",
    ],
    links: [{ label: "Open Settings", to: "/settings" }],
  },
  manageBranches: {
    icon: "🏛️",
    title: "Add / deactivate a school branch",
    summary: "SuperDev can always do this. Other roles can be granted the privilege via Settings → Maintenance → Role Permissions. Hard delete stays SuperDev-only regardless.",
    steps: [
      "Open Settings → Branches tab.",
      'Click "Add branch" — fill in Code (e.g. CEBU — A–Z, 0–9, underscore, dash), Name, Short name, Address, and optional logo URL.',
      "Save. The new branch appears in the school pill dropdown immediately.",
      "To temporarily hide a branch from the dropdown without losing its data, click Deactivate — historical rows still point at it.",
      "Reactivate any time via the same row.",
      "Hard delete is reserved for SuperDev and is blocked on the bootstrap branch (id=1). Use Deactivate when you only want to hide a branch.",
    ],
    links: [{ label: "Open Settings", to: "/settings" }],
  },
  deleteBranch: {
    icon: "🗑️",
    title: "Hard-delete a school branch (SuperDev only)",
    summary: "Permanently removes the branch row. Reserved for break-glass cleanup — orphans every row that referenced it. Prefer Deactivate.",
    steps: [
      "Open Settings → Branches tab.",
      "Find the branch row. The Delete button appears only for SuperDev and never on the bootstrap branch (id=1).",
      "Click Delete. A prompt asks you to type the branch's CODE exactly — this prevents accidental clicks.",
      "Type the code and confirm. The row vanishes from the catalog.",
      'Important: any students / employees / invoices / payments that pointed at this branch are NOT deleted — their school_id becomes orphaned. If you just want to "retire" a branch, click Cancel and use Deactivate instead.',
    ],
    links: [{ label: "Open Settings", to: "/settings" }],
  },
  rolePermissions: {
    icon: "🛡️",
    title: "Grant a role extra privileges (SuperDev only)",
    summary: "Delegate specific powers (add schools, manage content) to additional roles without raising them to a full Admin.",
    steps: [
      "Open Settings → Maintenance → Role Permissions.",
      'The card shows two panels: "Allow adding multiple schools" and "Allow creating contents".',
      "Tick the checkbox next to any role to grant it that privilege. Changes save immediately — no Save button.",
      "Untick to revoke. The role drops back to its default privileges.",
      "These checkboxes are PURELY ADDITIVE — they only ever grant, never reduce. Admin / SuperAdmin / SuperDev keep their built-in privileges either way.",
      "Hard delete of a school stays SuperDev-only regardless of what's checked. That blast radius is too high to delegate.",
      "SuperAdmin doesn't see this card by design — only the developer (SuperDev) controls the privilege matrix.",
    ],
    links: [{ label: "Open Settings", to: "/settings" }],
  },
  assignUserBranch: {
    icon: "🪪",
    title: "Assign a user to a specific branch",
    summary: "When you create a user, picking their branch makes them land in that school on login — they never see other branches' data.",
    steps: [
      "Open Settings → Users → Add user.",
      "Fill in the user's role and details.",
      'Use the "Branch" picker — pick the branch they should belong to. Leaving it on "Chain (no branch)" gives chain-tier visibility (only valid for SuperDev / SuperAdmin / Owner).',
      "Save. On their next login they're auto-scoped to that branch and the topbar pill is locked.",
    ],
    links: [{ label: "Open Settings", to: "/settings" }],
  },

  // ── Site content (ContentManager) ───────────────────────────────────
  siteContent: {
    icon: "🌐",
    title: "Edit the public website",
    summary: "Homepage, About, Programs, News, Contact, Footer — all editable under Site Content.",
    steps: [
      "Open Site Content → pick the section you want to edit.",
      "Make your changes. Save — the public site reflects them immediately.",
      "For News & Events, use Articles to create/edit posts.",
      "For photos, upload via the Media Library.",
    ],
    links: [
      { label: "Open Site Content", to: "/admin/site-content" },
      { label: "Open Articles", to: "/articles" },
    ],
  },
};

/** Role → list of guide keys. Drives what cards are rendered on the Help
 *  page for the logged-in user. Admin and SuperAdmin get the full set. */
const ROLE_GUIDES = {
  SuperDev: [
    "enroll", "directory", "recordPayment", "paymentHistory", "refund",
    "statementOfAccount", "recompute", "feeStructure", "discount",
    "reports", "assignTeacher", "section",
    "addUser", "assignUserBranch", "configBanks", "configSoaFooter", "backup",
    "switchBranch", "manageBranches", "deleteBranch", "rolePermissions",
    "announcements", "calendar",
  ],
  SuperAdmin: [
    "enroll", "directory", "recordPayment", "paymentHistory", "refund",
    "statementOfAccount", "recompute", "feeStructure", "discount",
    "reports", "assignTeacher", "section",
    "addUser", "assignUserBranch", "configBanks", "configSoaFooter", "backup",
    "switchBranch",
    "announcements", "calendar",
  ],
  Owner: [
    "directory", "statementOfAccount", "reports",
    "switchBranch",
    "announcements", "calendar",
  ],
  Admin: [
    "enroll", "directory", "recordPayment", "paymentHistory", "refund",
    "statementOfAccount", "recompute", "feeStructure", "discount",
    "reports", "assignTeacher", "section",
    "announcements", "calendar",
  ],
  AcademicHead: ["enroll", "assignTeacher", "section", "announcements", "calendar"],
  Registrar:      ["enroll", "directory", "announcements", "calendar"],
  RegistrarStaff: ["enroll", "directory", "announcements", "calendar"],
  Cashier:        ["recordPayment", "paymentHistory", "refund", "statementOfAccount", "announcements", "calendar"],
  CashierStaff:   ["recordPayment", "paymentHistory", "statementOfAccount", "announcements", "calendar"],
  Teacher:        ["viewSchedule", "announcements", "calendar"],
  Student:        ["viewMyTuition", "viewSchedule", "announcements", "calendar"],
  Parent:         ["viewChildTuition", "announcements", "calendar"],
  ContentManager: ["siteContent", "announcements", "calendar"],
  HR:             ["announcements", "calendar"],
  Librarian:      ["announcements", "calendar"],
  LibraryStaff:   ["announcements", "calendar"],
  Nurse:          ["announcements", "calendar"],
  ClinicStaff:    ["announcements", "calendar"],
  GuidanceOffice: ["announcements", "calendar"],
  Janitor:        ["announcements", "calendar"],
  SecurityGuard:  ["announcements", "calendar"],
  ComputerLab:    ["announcements", "calendar"],
  ComputerLabStaff: ["announcements", "calendar"],
  ScienceLab:     ["announcements", "calendar"],
  ScienceLabStaff: ["announcements", "calendar"],
};

/** Friendly label for the role pill in the header. */
function roleLabel(role) {
  const map = {
    SuperDev: "Super Developer", Owner: "Owner",
    SuperAdmin: "Super Admin", Admin: "Admin", AcademicHead: "Academic Head",
    Registrar: "Registrar", RegistrarStaff: "Registrar Staff",
    Cashier: "Cashier", CashierStaff: "Cashier Staff",
    Teacher: "Teacher", Student: "Student", Parent: "Parent / Guardian",
    ContentManager: "Content Manager", HR: "HR",
    Librarian: "Librarian", LibraryStaff: "Library Staff",
    Nurse: "Nurse", ClinicStaff: "Clinic Staff",
    GuidanceOffice: "Guidance Office", Janitor: "Janitor", SecurityGuard: "Security Guard",
    ComputerLab: "Computer Lab", ComputerLabStaff: "Computer Lab Staff",
    ScienceLab: "Science Lab", ScienceLabStaff: "Science Lab Staff",
  };
  return map[role] || role || "Guest";
}

export default function HelpPage() {
  const user = getUser();
  const navigate = useNavigate();
  const role = user?.role;

  // Track which guide cards are expanded. Multi-expand so the admin can
  // keep several open while comparing steps.
  const [expanded, setExpanded] = useState(() => new Set());
  const [query, setQuery] = useState("");

  const guideKeys = useMemo(() => ROLE_GUIDES[role] || [], [role]);

  const filteredKeys = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return guideKeys;
    return guideKeys.filter(k => {
      const g = GUIDES[k];
      if (!g) return false;
      const hay = [
        g.title, g.summary,
        ...(g.steps || []),
        ...(g.links || []).map(l => l.label),
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [guideKeys, query]);

  function toggle(k) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  }

  return (
    <div className={s.wrap}>
      <header className={s.header}>
        <div className={s.eyebrow}>HELP & GUIDES</div>
        <h1 className={s.title}>How to use the system</h1>
        <p className={s.sub}>
          Step-by-step guides for the tasks your role can do. Click any card
          to expand the steps, then use the link inside to jump straight to
          the right page.
        </p>
        <div className={s.metaRow}>
          <span className={s.rolePill}>{roleLabel(role)}</span>
          <span className={s.count}>
            {filteredKeys.length} guide{filteredKeys.length === 1 ? "" : "s"}
          </span>
        </div>
      </header>

      <div className={s.searchRow}>
        <input
          type="text"
          className={s.search}
          placeholder="Search guides (e.g. 'enroll', 'refund', 'SOA')…"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
      </div>

      {guideKeys.length === 0 ? (
        <div className={s.empty}>
          No role-specific guides configured for your account yet. If you need
          help, contact an administrator.
        </div>
      ) : filteredKeys.length === 0 ? (
        <div className={s.empty}>
          No guides match &quot;{query}&quot;. Try a different keyword.
        </div>
      ) : (
        <ul className={s.list}>
          {filteredKeys.map(k => {
            const g = GUIDES[k];
            if (!g) return null;
            const isOpen = expanded.has(k);
            return (
              <li key={k} className={`${s.card} ${isOpen ? s.cardOpen : ""}`}>
                <button type="button" className={s.cardHead}
                        onClick={() => toggle(k)}
                        aria-expanded={isOpen}>
                  <span className={s.icon} aria-hidden="true">{g.icon}</span>
                  <div className={s.headBody}>
                    <div className={s.cardTitle}>{g.title}</div>
                    <div className={s.cardSummary}>{g.summary}</div>
                  </div>
                  <span className={s.chevron} aria-hidden="true">
                    {isOpen ? "▾" : "▸"}
                  </span>
                </button>
                {isOpen && (
                  <div className={s.cardBody}>
                    <ol className={s.steps}>
                      {g.steps.map((step, i) => (
                        <li key={i} className={s.step}>{step}</li>
                      ))}
                    </ol>
                    {g.links && g.links.length > 0 && (
                      <div className={s.links}>
                        {g.links.map(l => (
                          <button
                            key={l.to}
                            type="button"
                            className={s.linkBtn}
                            onClick={() => navigate(l.to)}>
                            {l.label} →
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
